import React, { useCallback, useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import dayjs, { type Dayjs } from "dayjs";
import { useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";

import SvgLoadingAnimation from "../../../../components/loading";
import TopAppBarHome from "../../../../components/top-app-bar/TopAppBarHome";
import StatusIndicator from "../../../../components/status-indicator/StatusIndicator";
import {
  useGetProjectsQuery,
  useUpdateProjectMutation,
} from "../../../../app/services/projects";
import {
  useUpdateTaskMutation,
  type TaskBoard,
} from "../../../../app/services/taskBoard";
import { useShortcutAction } from "../../../../utils/keyboardShortcuts";
import type { ViewMode } from "../../../../types/home";
import {
  collectBoardMentions,
  computeBoardLatestDate,
  DATE_CHIP_COMPLETION,
  periodRange,
  replaceCellDate,
  type TaskCompletionFilter,
  type TaskPeriodFilter,
} from "./taskBoardUtils";
import MentionFilterBar from "./MentionFilterBar";
import CompletionFilterChips from "./CompletionFilterChips";
import PeriodFilterChips from "./PeriodFilterChips";
import ProjectFilterChips, {
  type ProjectChipOption,
} from "./ProjectFilterChips";
import ProjectTaskBoards from "./ProjectTaskBoards";
import GanttChart, { type GanttProjectBoard } from "./GanttChart";
import ActivityMonitorView from "../../../taskboard/components/ActivityMonitorView";
import { useTaskBoardSaveStatus } from "./useTaskBoardSaveStatus";
import { useActivityColors } from "./useActivityColors";

// 横断ビューでまとめて読み込むプロジェクト件数の上限。
const CROSS_PROJECT_LIMIT = 100;

/** 表示ビュー（セグメントコントロールの値。URL の `?view=` と同期する） */
type TaskBoardView = "table" | "gantt" | "activity";

// 完了フィルターの既定値。表は「残っている仕事」を、ガント・アクティビティは
// 「全体」を見るビューなので既定が異なる。フィルター自体は全ビュー共通の 1 つで、
// 既定は初期表示時と期間チップ解除時にだけ使う（ビュー切り替えでは変えない）。
const DEFAULT_COMPLETION_BY_VIEW: Record<TaskBoardView, TaskCompletionFilter> =
  {
    table: "incomplete",
    gantt: "all",
    activity: "all",
  };

const Container = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 96px;
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 64px 16px;
  width: 100%;
`;

const SectionsWrapper = styled.div<{ $hidden: boolean }>`
  display: ${({ $hidden }) => ($hidden ? "none" : "flex")};
  flex-direction: column;
  gap: 16px;
  /* フィルター中に全ボードがヒット 0 件のプロジェクトは中身が空になる。
     空のラッパーが gap ぶんの隙間として残らないよう畳む。 */
  & > :empty {
    display: none;
  }
`;

/**
 * 横断タスクボード（`/taskboard`）の本体。
 *
 * 上部はプロジェクトリスト・ショットリストと同じセグメントコントロール
 * （TopAppBarHome）で、タスク（表）/ スケジュール（ガント）/ 工数
 * （アクティビティ集計）/ 検索を切り替える（`?view=` と同期）。
 *
 * - 既定はアクセス可能な全アクティブプロジェクトの横断表示（テーブル）
 * - `?project=<projectId>` 付き（プロジェクト内から ⌥1 で遷移）のときは、
 *   そのプロジェクトのボードのみを表示する
 * - ⌥1 再押下はタスク → スケジュール → 工数 → タスク の循環
 *   （閲覧専用はスケジュール ⇄ 工数）
 *
 * フィルター（期間 / 完了 / 担当者 / プロジェクト）はすべて全ビュー共通で、
 * セグメントを切り替えても選択を維持する。
 * - 完了フィルター（すべて / 未完了 / 完了）: 既定は初期表示のビューに従う
 *   （表 = 未完了、ガント・アクティビティ = すべて）。ガントは完了ぶんを
 *   淡色 + 打ち消し線で描き、アクティビティは行単位で
 *   チェック済み = 実績 / 未チェック = 予定として絞り込む。
 * - 期間フィルター（すべて / 先週 / 本日 / 明日 / 今週 + 先月 / 今月 / 来月）:
 *   既定はすべて。日付チップは絞り込みと同時に完了フィルターを連動して
 *   切り替える（先週 = 完了、今週 = 未完了、本日・明日 = すべて、月 = すべて）。
 * - 担当者チップ（@）・プロジェクトチップ（バナーカラー）: アクティビティ集計
 *   では選択中ユーザーの実績・選択中プロジェクトの内訳へ絞り込む。表セクションは
 *   マウントを維持して `hidden` で畳み、ガント・担当者チップの母集合は
 *   選択中のプロジェクトのみから作る。
 */
const CrossProjectTaskBoard: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedMentions, setSelectedMentions] = useState<string[]>([]);
  // プロジェクトごとのボード本文 @メンション（各セクションから報告される）
  const [mentionsByProject, setMentionsByProject] = useState<
    Record<string, string[]>
  >({});
  // プロジェクトごとの全ボード（横断ガント・セクション順の算出に使う）
  const [boardsByProject, setBoardsByProject] = useState<
    Record<string, TaskBoard[]>
  >({});
  // 並列工程を縦積み表示するか（既定 ON。OFF = 従来の横並び。
  // 切り替えは各表のラベル行右端の 3 点リーダーメニューから）
  const [stackParallel, setStackParallel] = useState(true);
  // ── 表示ビュー（?view= と同期） ──
  const viewParam = searchParams.get("view");
  const view: TaskBoardView =
    viewParam === "gantt" || viewParam === "activity" ? viewParam : "table";

  // 完了フィルター（全プロジェクト・全ビュー共通）。未完了のみ / 完了のみ /
  // すべての3段階。初期値は最初に開いたビューの既定。
  // アクティビティは行単位（チェック済み = 実績、未チェック = 予定）で効く。
  const [completionFilter, setCompletionFilter] =
    useState<TaskCompletionFilter>(() => DEFAULT_COMPLETION_BY_VIEW[view]);
  // 期間フィルター（日付チップ + 月次）。null = すべて。
  const [period, setPeriod] = useState<TaskPeriodFilter | null>(null);
  // プロジェクトチップ（複数選択）。空 = 全プロジェクト表示。
  const [selectedProjectIds, setSelectedProjectIds] = useState<string[]>([]);
  // タスク名フィルター（部分一致）。空 = 絞り込みなし。セグメント行の検索
  // アイコン（オーバーレイ検索）から渡され、表・ガント・アクティビティに効く。
  const [taskNameQuery, setTaskNameQuery] = useState("");
  // ⌥←/→（ボードのタブ切り替え）の対象プロジェクト = 最後に操作した
  // セクション（pointerdown / pointerenter / focusin で更新）。
  const [shortcutProjectId, setShortcutProjectId] = useState<string | null>(
    null,
  );

  const setView = useCallback(
    (next: TaskBoardView) => {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          if (next === "table") params.delete("view");
          else params.set("view", next);
          return params;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  // アクティビティ集計と同じ配色（担当者・列）をタスクボードにも反映する。
  const { staffColorOf, columnColorOf } = useActivityColors();

  // 保存状態の集約表示（保存中... / 保存済み / 保存に失敗しました）。
  // 各セクション・ガントの mutation を in-flight カウンタでまとめる。
  const { status: saveStatus, onSaveStart, onSaveEnd } =
    useTaskBoardSaveStatus();

  const { data, isLoading, error } = useGetProjectsQuery({
    status: "active",
    limit: CROSS_PROJECT_LIMIT,
  });

  const [updateTask] = useUpdateTaskMutation();
  const [updateProject] = useUpdateProjectMutation();

  // タスクボードにアクセスできる（共有設定で edit / view が付与された）
  // プロジェクトのみを横断ボードの対象にする。none は表示しない。
  const accessibleProjects = useMemo(
    () =>
      (data?.data?.projects || []).filter(
        (p) => p.task_board_access !== "none",
      ),
    [data],
  );

  // ── 単一プロジェクト絞り込み（?project=<uuid>） ──
  // プロジェクト内から ⌥1 で開いたとき、そのプロジェクトのボードのみ表示する。
  // アクセスできないプロジェクト ID が指定された場合は無視して横断表示にする。
  const focusParam = searchParams.get("project");
  const focusProjectId =
    focusParam && accessibleProjects.some((p) => p.project_id === focusParam)
      ? focusParam
      : null;

  // プロジェクトのセクション順をタスク行と同じ基準で揃える。
  // 各ボードの最終期日（全タスクの最遅日付）の昇順で、期日が近いプロジェクトを
  // 先頭にする。ボード未読込・日付なしのプロジェクトは末尾（安定ソートのため
  // 相互の順序は API 順 = 最終更新の新しい順を維持）。
  const sortedProjects = useMemo(() => {
    const latestByProject = new Map<string, string | null>();
    for (const project of accessibleProjects) {
      const boards = boardsByProject[project.project_id];
      // 複数ボードのプロジェクトは、全ボードで最も近い期日を代表値にする。
      const latest = (boards ?? [])
        .map((board) => computeBoardLatestDate(board.tasks))
        .filter((value): value is string => !!value)
        .sort()[0] ?? null;
      latestByProject.set(project.project_id, latest);
    }
    return [...accessibleProjects].sort((a, b) => {
      const da = latestByProject.get(a.project_id);
      const db = latestByProject.get(b.project_id);
      if (da === db) return 0;
      if (!da) return 1;
      if (!db) return -1;
      return da < db ? -1 : 1;
    });
  }, [accessibleProjects, boardsByProject]);

  // 表・ガント・メンション集約の対象プロジェクト。
  // 単一プロジェクト絞り込み時は該当プロジェクトのみ。
  const displayProjects = useMemo(
    () =>
      focusProjectId
        ? sortedProjects.filter((p) => p.project_id === focusProjectId)
        : sortedProjects,
    [sortedProjects, focusProjectId],
  );

  // 選択中のプロジェクトが一覧から消えた（アーカイブ等）ときは選択から外す。
  useEffect(() => {
    setSelectedProjectIds((prev) => {
      const next = prev.filter((id) =>
        accessibleProjects.some((p) => p.project_id === id),
      );
      return next.length === prev.length ? prev : next;
    });
  }, [accessibleProjects]);

  const handleToggleProject = useCallback(
    (projectId: string) =>
      setSelectedProjectIds((prev) =>
        prev.includes(projectId)
          ? prev.filter((id) => id !== projectId)
          : [...prev, projectId],
      ),
    [],
  );

  // プロジェクトチップ適用後の対象プロジェクト。
  // 表セクションは全プロジェクトをマウントしたまま（データ供給の継続のため）
  // `hidden` で出し分けるので、こちらはメンション集約・ガントにのみ使う。
  const projectScopedProjects = useMemo(() => {
    if (selectedProjectIds.length === 0) return displayProjects;
    const filtered = displayProjects.filter((p) =>
      selectedProjectIds.includes(p.project_id),
    );
    return filtered.length > 0 ? filtered : displayProjects;
  }, [displayProjects, selectedProjectIds]);

  // プロジェクトチップの選択肢。並びはリスト表示（最終期日順）と同じ。
  const projectChipOptions = useMemo<ProjectChipOption[]>(
    () =>
      sortedProjects.map((p) => ({
        id: p.project_id,
        name: p.code_name,
        bannerColor: p.banner_color,
      })),
    [sortedProjects],
  );

  // 各プロジェクトの本文メンションを集約して担当者チップの母集合にする（全ビュー
  // 共通）。各セクションは完了フィルター・期間を適用したタスクからのみメンションを
  // 報告するため、該当タスクが無い担当者のチップはここに並ばない。
  const filteredMentions = useMemo<string[]>(() => {
    const result: string[] = [];
    for (const project of projectScopedProjects) {
      for (const name of mentionsByProject[project.project_id] || []) {
        if (!result.includes(name)) result.push(name);
      }
    }
    return result;
  }, [projectScopedProjects, mentionsByProject]);

  const handleReportMentions = useCallback(
    (projectId: string, mentions: string[]) => {
      setMentionsByProject((prev) => {
        const current = prev[projectId];
        if (
          current &&
          current.length === mentions.length &&
          current.every((m, i) => m === mentions[i])
        ) {
          return prev;
        }
        return { ...prev, [projectId]: mentions };
      });
    },
    [],
  );

  const handleReportBoards = useCallback(
    (projectId: string, boards: TaskBoard[] | null) => {
      setBoardsByProject((prev) => {
        if (!boards) {
          if (!(projectId in prev)) return prev;
          const next = { ...prev };
          delete next[projectId];
          return next;
        }
        // 参照同一なら更新を抑制（無限ループ防止）
        if (prev[projectId] === boards) return prev;
        return { ...prev, [projectId]: boards };
      });
    },
    [],
  );

  // ガント用に、表示対象プロジェクト（プロジェクトチップ適用後）の
  // ボードのみ集める（表セクションと同じ最終期日順で並べる）
  const ganttBoards = useMemo<GanttProjectBoard[]>(() => {
    const result: GanttProjectBoard[] = [];
    for (const project of projectScopedProjects) {
      const boards = boardsByProject[project.project_id];
      if (!boards || boards.length === 0) continue;
      boards.forEach((board, index) => {
        result.push({
          projectId: project.project_id,
          boardId: board.id,
          projectName: project.code_name,
          bannerColor: project.banner_color,
          columns: board.columns,
          tasks: board.tasks,
          // 全体スケジュール・マイルストーンはプロジェクト単位の情報なので、
          // 先頭ボードの見出しにだけ載せる（ボードごとに重複させない）。
          startDate: index === 0 ? project.start_date : null,
          endDate: index === 0 ? project.end_date : null,
          milestones: index === 0 ? project.milestones : [],
          // 閲覧のみ（view）はドラッグでの日付変更を不可にする。
          canEdit:
            project.task_board_access === "edit" && board.access !== "view",
        });
      });
    }
    return result;
  }, [projectScopedProjects, boardsByProject]);

  // ── コードネーム絞り込み ──
  // 絞り込み入力がプロジェクトのコードネームに部分一致（大文字小文字無視）する場合、
  // 「一致したボードのみを全タスク表示」するモードに切り替える。
  // 一致がなければ従来どおりタスク名の部分一致で行を絞り込む。
  const normalizedQuery = taskNameQuery.trim().toLowerCase();
  const codeNameMatchedIds = useMemo<Set<string> | null>(() => {
    if (!normalizedQuery) return null;
    const ids = displayProjects
      .filter((p) =>
        (p.code_name ?? "").toLowerCase().includes(normalizedQuery),
      )
      .map((p) => p.project_id);
    return ids.length > 0 ? new Set(ids) : null;
  }, [normalizedQuery, displayProjects]);

  // コードネーム一致モード中はタスク行のタスク名フィルターを解除する
  // （担当者・完了フィルターは適用されたまま）。
  const effectiveTaskNameFilter = codeNameMatchedIds ? "" : taskNameQuery;

  // ガントもコードネーム一致モード中は対象ボードのみに絞る。
  const visibleGanttBoards = useMemo(
    () =>
      codeNameMatchedIds
        ? ganttBoards.filter((b) => codeNameMatchedIds.has(b.projectId))
        : ganttBoards,
    [ganttBoards, codeNameMatchedIds],
  );

  // 完了フィルター・期間を通していない全タスクのメンション。担当者の選択を
  // 自動で外す基準に使う（フィルター切り替えだけでは選択を外さないため）。
  const allBoardMentions = useMemo<string[]>(() => {
    const result: string[] = [];
    for (const board of visibleGanttBoards) {
      for (const name of collectBoardMentions(board.tasks)) {
        if (!result.includes(name)) result.push(name);
      }
    }
    return result;
  }, [visibleGanttBoards]);

  // 編集可能なボードが 1 つもない（すべて閲覧のみ）場合は、編集を伴う
  // タスク（表）ビューを出さない。セグメントはスケジュール / 工数だけになる
  // （閲覧専用ユーザー向け）。
  const hasEditableBoard = displayProjects.some(
    (p) => p.task_board_access === "edit",
  );
  const noTableView = !hasEditableBoard;

  // 実効ビュー。閲覧専用でタスク（表）は選べないため、既定はガントへ倒す。
  const effectiveView: TaskBoardView =
    noTableView && view === "table" ? "gantt" : view;
  const showGanttView = effectiveView === "gantt";
  const showActivity = effectiveView === "activity";

  // ── 期間フィルター（全ビュー共通） ──
  // 期間 → 絞り込み範囲（表・ガント）。未選択なら null（絞り込みなし）。
  const dateRange = useMemo(
    () => (period ? periodRange(period) : null),
    [period],
  );

  // 期間の選択と同時に、完了フィルターを連動させる。
  // 解除（すべて）時は表示中のビューの既定へ戻す。
  const handlePeriodChange = useCallback(
    (next: TaskPeriodFilter | null) => {
      setPeriod(next);
      if (next?.kind === "chip") {
        setCompletionFilter(DATE_CHIP_COMPLETION[next.chip]);
      } else if (next?.kind === "month") {
        // 月はやったこと・やることの両方を見る期間なので「すべて」にする
        setCompletionFilter("all");
      } else {
        setCompletionFilter(DEFAULT_COMPLETION_BY_VIEW[effectiveView]);
      }
    },
    [effectiveView],
  );

  // ⌥←/→ の対象プロジェクト。最後に操作したセクションが見えていればそれ、
  // 無ければ単一プロジェクト絞り込み（?project=）→ 表示中の先頭、の順で決める。
  // テーブル以外の表示中は表のタブが見えないので対象なし（ショートカット無効）。
  const shortcutTargetProjectId = useMemo(() => {
    if (effectiveView !== "table") return null;
    const visibleIds = displayProjects
      .filter(
        (p) =>
          (!codeNameMatchedIds || codeNameMatchedIds.has(p.project_id)) &&
          (selectedProjectIds.length === 0 ||
            selectedProjectIds.includes(p.project_id)),
      )
      .map((p) => p.project_id);
    if (shortcutProjectId && visibleIds.includes(shortcutProjectId)) {
      return shortcutProjectId;
    }
    if (focusProjectId && visibleIds.includes(focusProjectId)) {
      return focusProjectId;
    }
    return visibleIds[0] ?? null;
  }, [
    effectiveView,
    displayProjects,
    codeNameMatchedIds,
    selectedProjectIds,
    shortcutProjectId,
    focusProjectId,
  ]);

  const handleShortcutFocus = useCallback(
    (projectId: string) => setShortcutProjectId(projectId),
    [],
  );

  // 担当者チップの並び（filteredMentions）。母集合に無い選択中の担当者も末尾に
  // 残し、完了フィルター等の切り替えで選択が見えなくならないようにする。
  const mentionChips = useMemo(() => {
    const missing = selectedMentions.filter(
      (name) => !filteredMentions.includes(name),
    );
    return missing.length > 0
      ? [...filteredMentions, ...missing]
      : filteredMentions;
  }, [filteredMentions, selectedMentions]);

  // ボードのどこにも登場しなくなったメンション（タスク削除・プロジェクト
  // チップ切替等）は選択からも自動で外す。基準は完了を含む全タスク由来の
  // allBoardMentions なので、フィルターやビューの切り替えでは外れない。
  // 未報告（ロード中）のプロジェクトがある間は母集合が揃っていないため待つ。
  const allBoardsReported = displayProjects.every(
    (p) => p.project_id in mentionsByProject,
  );
  useEffect(() => {
    if (!allBoardsReported) return;
    setSelectedMentions((prev) => {
      const next = prev.filter((name) => allBoardMentions.includes(name));
      return next.length === prev.length ? prev : next;
    });
  }, [allBoardsReported, allBoardMentions]);

  const handleToggleMention = (name: string) =>
    setSelectedMentions((prev) =>
      prev.includes(name) ? prev.filter((n) => n !== name) : [...prev, name],
    );

  // ドラッグでの日付変更（ドット/マイルストーン）を許可するプロジェクトを返す。
  // 閲覧のみ（view）や対象外のプロジェクトは undefined = 変更しない。
  const findEditableProject = useCallback(
    (projectId: string) =>
      accessibleProjects.find(
        (p) => p.project_id === projectId && p.task_board_access === "edit",
      ),
    [accessibleProjects],
  );

  /**
   * ガントのドット ドラッグで日付を変更したときの永続化。対象タスク/列のセル本文中の
   * `fromDate` 表記を `toDate` に書式維持で置換して updateTask に投げる。
   * 横断ガントは複数プロジェクトのタスクを扱うため、各セクション側ではなく
   * 横断オーナーであるここで mutation を呼ぶ（cache 無効化は project_id 単位で効く）。
   */
  const handleMoveMarker = useCallback(
    async (params: {
      projectId: string;
      boardId: string;
      taskId: string;
      columnId: string;
      fromDate: Dayjs;
      toDate: Dayjs;
    }) => {
      if (!findEditableProject(params.projectId)) return;
      const board = (boardsByProject[params.projectId] ?? []).find(
        (b) => b.id === params.boardId,
      );
      if (!board) return;
      const task = board.tasks.find((t) => t.id === params.taskId);
      if (!task) return;
      const cell = task.cells?.[params.columnId];
      if (!cell) return;
      const nextContent = replaceCellDate(
        cell.content ?? "",
        params.fromDate,
        params.toDate,
        dayjs(),
      );
      if (nextContent === cell.content) return;
      const nextCells = {
        ...(task.cells || {}),
        [params.columnId]: { ...cell, content: nextContent },
      };
      onSaveStart();
      try {
        await updateTask({
          project_id: params.projectId,
          board_id: params.boardId,
          task_id: params.taskId,
          cells: nextCells,
        }).unwrap();
        onSaveEnd(true);
      } catch {
        onSaveEnd(false);
        toast.error("日付の更新に失敗しました");
      }
    },
    [findEditableProject, boardsByProject, updateTask, onSaveStart, onSaveEnd],
  );

  /**
   * ガントのマイルストーン ドラッグで日付を変更したときの永続化。
   * プロジェクト更新 API の milestones は全置換のため、対象要素の日付だけを
   * ドラッグ日数分シフトした配列全体を PATCH する（時刻成分は元の値を保持）。
   * 成功時は Projects タグの無効化で一覧が再取得され、ガントに反映される。
   */
  const handleMoveMilestone = useCallback(
    async (params: {
      projectId: string;
      milestoneIndex: number;
      fromDate: Dayjs;
      toDate: Dayjs;
    }) => {
      const project = findEditableProject(params.projectId);
      if (!project) return;
      const milestones = project.milestones ?? [];
      const target = milestones[params.milestoneIndex];
      if (!target?.date) return;
      const days = params.toDate.diff(params.fromDate, "day");
      if (days === 0) return;
      const nextDate = dayjs(target.date).add(days, "day");
      if (!nextDate.isValid()) return;
      onSaveStart();
      try {
        await updateProject({
          project_id: params.projectId,
          milestones: milestones.map((m, i) =>
            i === params.milestoneIndex
              ? { event: m.event, date: nextDate.toISOString() }
              : { event: m.event, date: m.date },
          ),
        }).unwrap();
        onSaveEnd(true);
      } catch {
        onSaveEnd(false);
        toast.error("マイルストーンの更新に失敗しました");
      }
    },
    [findEditableProject, updateProject, onSaveStart, onSaveEnd],
  );

  // ⌥1 再押下（既にタスクボード表示中）: セグメントの並びどおり
  // タスク → スケジュール → 工数 → タスク と循環する（閲覧専用はタスクを除く）。
  // 注意: 早期 return（ローディング等）より前に呼ぶ必要がある（rules-of-hooks）。
  useShortcutAction("taskboard-cycle", () => {
    const order: TaskBoardView[] = noTableView
      ? ["gantt", "activity"]
      : ["table", "gantt", "activity"];
    const index = order.indexOf(effectiveView);
    setView(order[(index + 1) % order.length]);
  });

  // セグメントコントロール（タスク / スケジュール / 工数）。
  // 閲覧専用（編集可能なボードなし）ではタスク（表）を出さない。
  const viewOptions = useMemo(() => {
    const options: { label: string; value: ViewMode }[] = [];
    // ラベルは見た目（形式）ではなく目的で名付ける:
    // 「何を（タスク）・いつ（スケジュール）・どれだけ（工数）」
    if (!noTableView) options.push({ label: "タスク", value: "table" });
    options.push({ label: "スケジュール", value: "gantt" });
    options.push({ label: "工数", value: "activity" });
    return options;
  }, [noTableView]);

  // セグメント切り替え。検索モード（"search"）は TopAppBarHome 内の状態で、
  // onSearchChange 経由で taskNameQuery に反映されるためここでは無視する。
  const handleViewChange = useCallback(
    (mode: ViewMode) => {
      if (mode === "table" || mode === "gantt" || mode === "activity") {
        setView(mode);
      }
    },
    [setView],
  );

  if (isLoading) {
    return (
      <div className="u-col-center u-w100 u-mt16">
        <SvgLoadingAnimation />
      </div>
    );
  }

  if (error) {
    return <div>エラーが発生しました</div>;
  }

  if (accessibleProjects.length === 0) {
    return (
      <EmptyState>
        <span
          className="material-symbols-rounded"
          style={{ fontSize: 48, color: "var(--soremo-light-gray)" }}
        >
          event_busy
        </span>
        <p className="bodytext-13 u-text-light-gray">
          表示できるタスクボードがありません
        </p>
      </EmptyState>
    );
  }

  return (
    <>
      {/* セグメント行は wrapper の外（全幅 + 自身の padding 16px）に置く
          （プロジェクトリスト・ショットリストと共通のデザイン）。
          検索はアイコンからのオーバーレイ検索（タスク名・コードネーム）。 */}
      <TopAppBarHome
        options={viewOptions}
        defaultValue={noTableView ? "gantt" : "table"}
        value={effectiveView}
        onChange={handleViewChange}
        showSearchInput
        onSearchChange={setTaskNameQuery}
        searchPlaceholder="タスク名・コードネームで絞り込み"
      />
      <div className="u-wrapper-full u-pt16">
        <Container>
          {/* フィルターバー（上から期間チップ / 完了フィルター / 担当者チップ /
              プロジェクトチップの 4 行）。期間・完了・担当者・プロジェクトの
              全フィルターが全ビュー共通で効き、ビューを切り替えても維持する。
              閲覧専用（タスク表なし）のガント表示では完了フィルターだけを出す。 */}
          <div className="u-col u-gap8">
            {(showActivity || !noTableView) && (
              <PeriodFilterChips
                value={period}
                onChange={handlePeriodChange}
              />
            )}
            {/* アクティビティでは行単位（チェック済み = 実績 / 未チェック = 予定）
                で絞り込む。 */}
            <CompletionFilterChips
              value={completionFilter}
              onChange={setCompletionFilter}
            />
            {(showActivity || !noTableView) && (
              <MentionFilterBar
                mentions={mentionChips}
                selected={selectedMentions}
                onToggle={handleToggleMention}
                onClear={() => setSelectedMentions([])}
                showHelp={effectiveView === "table"}
                colorOf={staffColorOf}
              />
            )}
            {/* 単一プロジェクト絞り込み（?project=）中はプロジェクトチップを出さない */}
            {(showActivity || !noTableView) && !focusProjectId && (
              <ProjectFilterChips
                projects={projectChipOptions}
                selected={selectedProjectIds}
                onToggle={handleToggleProject}
                onClear={() => setSelectedProjectIds([])}
              />
            )}
          </div>

          {showActivity && (
            <ActivityMonitorView
              period={period}
              completionFilter={completionFilter}
              searchQuery={taskNameQuery}
              selectedMentions={selectedMentions}
              projectIds={
                focusProjectId ? [focusProjectId] : selectedProjectIds
              }
            />
          )}

          {showGanttView && (
            <GanttChart
              boards={visibleGanttBoards}
              selectedMentions={selectedMentions}
              taskNameFilter={effectiveTaskNameFilter}
              completionFilter={completionFilter}
              dateRange={dateRange}
              onMoveMarker={handleMoveMarker}
              onMoveMilestone={handleMoveMilestone}
            />
          )}

          {/* タスク表セクションは常にマウントし、ガント・アクティビティ表示中や
              閲覧専用時は視覚的に隠す（API 取得・boardsByProject の供給を継続する
              ため）。 */}
          <SectionsWrapper $hidden={effectiveView !== "table"}>
            {displayProjects.map((project) => (
              <ProjectTaskBoards
                key={project.project_id}
                projectId={project.project_id}
                projectName={project.code_name}
                bannerColor={project.banner_color}
                position={project.current_user_role}
                noDeliverable={project.total_shots_count === 0}
                canEdit={project.task_board_access === "edit"}
                stackParallel={stackParallel}
                onToggleStackParallel={() => setStackParallel((v) => !v)}
                completionFilter={completionFilter}
                selectedMentions={selectedMentions}
                taskNameFilter={effectiveTaskNameFilter}
                dateRange={dateRange}
                mentionColorOf={staffColorOf}
                columnColorOf={columnColorOf}
                hidden={
                  (codeNameMatchedIds
                    ? !codeNameMatchedIds.has(project.project_id)
                    : false) ||
                  // プロジェクトチップで選択中は選択外のプロジェクトを畳む
                  // （マウントは維持してデータ供給・ガントへの報告は続ける）
                  (selectedProjectIds.length > 0 &&
                    !selectedProjectIds.includes(project.project_id))
                }
                onReportMentions={handleReportMentions}
                onReportBoards={handleReportBoards}
                onSaveStart={onSaveStart}
                onSaveEnd={onSaveEnd}
                isShortcutTarget={
                  shortcutTargetProjectId === project.project_id
                }
                onShortcutFocus={handleShortcutFocus}
              />
            ))}
          </SectionsWrapper>

          {/* 保存状態の集約表示（画面右下固定） */}
          <StatusIndicator status={saveStatus} />
        </Container>
      </div>
    </>
  );
};

export default CrossProjectTaskBoard;
